/**
 * Aurora's orb, in the shared 120×120 LINE asset box.
 *
 * The theme's hero object is a porthole onto its own night: a dark disc with
 * bands of aurora sweeping through it and a few stars. It is drawn in TWO places
 * — the passcode dots (line-passcode) and the Home tab button (line-menu-button)
 * — so the geometry lives here and both import it, the way the Everest summit
 * does in line-peak-glyph. Two components owning copies of one motif is exactly
 * how the Everest passcode drifted out of step with its exporter.
 */

/** Radius of the disc inside the 120 box. */
export const ORB_R = 38;

/** Narrow diagonal sweeps, meant to be CLIPPED by the rim so no end ever shows.
 *  A band filling half the orb reads as a planet; what has to read is a night
 *  sky with light crossing it, so most of the circle stays dark. */
export const ORB_BANDS = [
  'M 2 96 C 26 78 44 68 62 52 C 74 42 92 34 118 24 L 118 38 C 94 50 80 56 68 66 C 50 82 30 98 2 110 Z',
  'M 2 118 C 28 100 48 90 66 74 C 80 62 98 54 120 46 L 120 55 C 100 65 86 70 74 80 C 58 94 32 114 2 126 Z',
  'M 2 74 C 24 58 42 52 60 38 C 72 28 92 20 118 8 L 118 19 C 96 30 80 36 68 46 C 52 60 28 80 2 90 Z',
];

/** The aurora trio, in the order the theme's own palette lists them. */
export const ORB_COLORS = [ '#1ECFB6', '#549EFF', '#BAA5FF' ];

/** The lit rim runs the whole sweep rather than one colour. */
export const ORB_RIM_STOPS = [
  { off: 0, color: '#549EFF' },      // blue
  { off: 0.34, color: '#1ECFB6' },   // green
  { off: 0.67, color: '#BB719B' },   // pink
  { off: 1, color: '#BAA5FF' },      // purple
];

/** The unlit rim: the same navy drained of its colour. */
export const ORB_RIM_OFF = '#2C4A63';

/** Stars inside the orb — few and bright; specks just read as dust. */
export const ORB_STARS = [
  [ { cx: 42, cy: 38, r: 2.8 }, { cx: 82, cy: 34, r: 2.1 }, { cx: 33, cy: 70, r: 1.9 } ],
  [ { cx: 76, cy: 42, r: 2.6 }, { cx: 38, cy: 78, r: 2 } ],
  [ { cx: 52, cy: 31, r: 2.4 }, { cx: 88, cy: 60, r: 1.8 }, { cx: 34, cy: 56, r: 2.7 } ],
  [ { cx: 31, cy: 48, r: 2.7 }, { cx: 74, cy: 28, r: 1.9 } ],
];

/** Which bands appear in which colour, per passcode digit. */
export const ORB_SETS: { band: number; color: number; o: number }[][] = [
  [ { band: 0, color: 1, o: 0.85 }, { band: 1, color: 0, o: 0.5 }, { band: 2, color: 2, o: 0.32 } ],
  [ { band: 2, color: 0, o: 0.8 }, { band: 0, color: 2, o: 0.45 } ],
  [ { band: 1, color: 2, o: 0.8 }, { band: 0, color: 0, o: 0.5 }, { band: 2, color: 1, o: 0.3 } ],
  [ { band: 0, color: 0, o: 0.85 }, { band: 2, color: 1, o: 0.42 } ],
];

/** The Home TAB's own sky: blue-led (owner 2026-08-12), so the active tab reads
 *  as the theme's blue at a glance while still carrying the other two. */
export const ORB_TAB_SET = [
  { band: 0, color: 1, o: 0.95 },
  { band: 2, color: 0, o: 0.45 },
  { band: 1, color: 2, o: 0.3 },
];
